import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getOnboardingStatus } from '@/services/api'
import { StatusBadge } from '@/components/StatusBadge'
import { PipelineProgress } from '@/components/PipelineProgress'

export default function Status() {
  const { id } = useParams<{ id: string }>()

  const { data, isLoading, error } = useQuery({
    queryKey: ['onboarding-status', id],
    queryFn: () => getOnboardingStatus(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      // Poll every 10s if status is running
      return query.state.data?.status === 'running' ? 10000 : false
    },
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Erreur</h1>
          <p className="mt-2 text-muted-foreground">Impossible de charger le statut de la demande</p>
          <Link to="/" className="text-gold hover:text-gold/80 text-sm font-medium mt-4 inline-block">
            &larr; Retour à l'accueil
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-bronze">
        <div className="container mx-auto px-4 py-6">
          <Link to="/" className="text-white/70 hover:text-white text-sm font-medium">
            &larr; Retour à l'accueil
          </Link>
          <div className="flex items-center justify-between mt-2">
            <h1 className="text-xl lg:text-2xl font-bold text-white">
              Statut de l'Onboarding
            </h1>
            <StatusBadge status={data?.status || 'unknown'} />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Status Card */}
          <div className="bg-white rounded-lg shadow-sm border border-border p-6 mb-8">
            <h2 className="text-lg font-semibold text-bronze mb-4 pb-2 border-b border-border">
              Détails de la Demande
            </h2>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Nom du Client</dt>
                <dd className="mt-1 text-lg font-semibold text-bronze">{data?.client_name}</dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-muted-foreground">ID de la Demande</dt>
                <dd className="mt-1 font-mono text-sm text-foreground">{id}</dd>
              </div>

              <div>
                <dt className="text-sm font-medium text-muted-foreground">Date de Soumission</dt>
                <dd className="mt-1 text-foreground">
                  {data?.created_at && new Date(data.created_at).toLocaleString('fr-FR')}
                </dd>
              </div>

              {data?.approved_at && (
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Date d'Approbation</dt>
                  <dd className="mt-1 text-foreground">
                    {new Date(data.approved_at).toLocaleString('fr-FR')}
                  </dd>
                </div>
              )}

              {data?.completed_at && (
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Date de Finalisation</dt>
                  <dd className="mt-1 text-foreground">
                    {new Date(data.completed_at).toLocaleString('fr-FR')}
                  </dd>
                </div>
              )}

              {data?.environment_url && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-muted-foreground">URL de l'Environnement</dt>
                  <dd className="mt-1">
                    <a
                      href={data.environment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold hover:text-gold/80 font-medium"
                    >
                      {data.environment_url}
                    </a>
                  </dd>
                </div>
              )}

              {data?.error_message && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-destructive">Message d'Erreur</dt>
                  <dd className="mt-1 text-destructive bg-destructive/10 p-3 rounded">
                    {data.error_message}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Pipeline Progress - Only shown when approved or later */}
          {id && data?.status && data.status !== 'pending' && (
            <div className="bg-white rounded-lg shadow-sm border border-border p-6 mb-8">
              <PipelineProgress requestId={id} overallStatus={data.status} />
            </div>
          )}

          {/* Help text */}
          <p className="text-sm text-muted-foreground text-center">
            Cette page se rafraichit automatiquement pendant le provisionnement.
            Conservez ce lien pour suivre l'avancement de votre demande.
          </p>
        </div>
      </main>
    </div>
  )
}
